import React from "react";
import { useDispatch } from "react-redux";
import axios from "axios";

// material-ui
import makeStyles from "@material-ui/styles/makeStyles";
import Paper from "@material-ui/core/Paper";

// jaza soft
import { PageHeader, SimpleForm, TextInput, SelectArrayInput, translate, required, showNotification } from "jazasoft";

// helper
import useQuery from "../../../hooks/useQuery";
import useMutation from "../../../hooks/useMutation";
import { authServerUrl } from "../../../App.jsx";
import { inputOptions, listFromCsv } from "../../../utils/helpers";
import hasPrivilege from "../../../utils/hasPrivilege";
import Forbidden from "../../../components/Forbidden";
import useUnit from "../../../hooks/useUnit";
import { useSetting } from "../../../hooks";
import { CustomersFields } from "../../../utils/entityFields.js";
import SettingKeys from "../../../utils/SettingKeys.jsx";
import getValidation from "../../../utils/getValidation.js";

const useStyles = makeStyles(theme => ({
  container: {
    margin: theme.spacing(3),
    padding: theme.spacing(3)
  }
}));

const CustomerEdit = props => {
  const { translate, resource, id, i18nKey } = props;
  const classes = useStyles();

  // api call
  const { data: apis = {} } = useQuery({ url: "apis", params: { appId: "iam" } });
  const { data: customer = {} } = useQuery({ url: `customers/${id}` });

  const api = apis[0] || {};

  const fields = useSetting(SettingKeys.ENTITY_FIELDS, resource);
  const validationRules = useSetting(SettingKeys.ENTITY_FIELD_VALIDATION, resource);

  const dispatch = useDispatch();
  const [mutate] = useMutation();
  const { unitChoices: unitList } = useUnit(customer);

  const onSubmit = values => {
    let options = {
      url: `${resource}/${id}`,
      method: "put",
      data: { ...values, unitIds: values?.unitIds?.join(",") }
    };
    // Update in app first, then in IAM
    mutate(options, {
      onSuccess: async customer => {
        options = {
          url: `${authServerUrl}/permissions`,
          method: "put",
          data: { key: "customerId", name: customer.name, value: customer.id },
          headers: { "x-api-key": api?.apiKey }
        };
        const response = await axios.request(options);
        if (response.status === 201 || response.status === 200) {
          dispatch(showNotification("js.notification.updated", { messageArgs: { smart_count: 1, element: "Customer" } }));
        }
        props.history.push(`/library/${resource}`);
      },
      onFailure: () => {
        props.history.push(`/library/${resource}`);
      }
    });
  };

  if (!hasPrivilege(props.roles, props.hasAccess, "customer", "update")) {
    return <Forbidden history={props.history} />;
  }

  return (
    <div>
      <PageHeader title={translate("resources.customers.page.edit")} titleArgs={{ _: "Edit Customer" }} />
      <Paper className={classes.container}>
        <SimpleForm onSubmit={onSubmit} record={{ ...customer, unitIds: listFromCsv(customer?.unitIds) }} i18nKey={i18nKey}>
          {unitList?.length > 1 && (
            <SelectArrayInput validate={[required()]} source="unitIds" label="Units" choices={unitList} {...inputOptions(3)} />
          )}
          {fields.includes(CustomersFields.NAME) && (
            <TextInput
              source="name"
              validate={getValidation(validationRules[CustomersFields.NAME])}
              options={{ ...inputOptions(3), disabled: customer && customer.isReferenced }}
            />
          )}
          {fields.includes(CustomersFields.CODE) && (
            <TextInput source="code" validate={getValidation(validationRules[CustomersFields.CODE])} {...inputOptions(3)} />
          )}
          {fields.includes(CustomersFields.DESC) && (
            <TextInput source="desc" validate={getValidation(validationRules[CustomersFields.DESC])} {...inputOptions(3)} />
          )}
        </SimpleForm>
      </Paper>
    </div>
  );
};

export default translate(CustomerEdit);
